/**
 * Quality Command Center — Operational Review Workflow
 *
 * (formerly "QC Dashboard" — rebranded Jun 14, 2026 / QCC-001)
 *
 * Tabs: Files Awaiting Review | Unassigned | Correction Required | Approved Today | Critical Returns Today
 * KPIs: same five tiles, premium `.kpi-card` styling shared with Admin Dashboard
 * Features: TitleFlow brand anchor, row-level urgency, one-click actions,
 *           workforce-management cluster (Active / Break / Downtime / My Workspace).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import {
  CheckCircle,
  Clock,
  User,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  ArrowUpRight,
  Search,
  ChevronDown,
  UserPlus,
  UserMinus,
  RotateCcw,
  LogOut,
  FileText,
  UserRoundX,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../App';
import { showGlassmorphismWelcome } from '../utils/glassmorphismToasts';
import { SessionHealthIndicator, BreakButton, SystemDowntimeButton } from '../components/session';
import TitleFlowLogo from '../components/TitleFlowLogo';
import NotificationBell from '../components/shared/NotificationBell';
import OpsIntroOverlay from '../components/dashboard/OpsIntroOverlay';
import QCApproveModal from '../components/qc/QCApproveModal';
import QCReturnModal from '../components/qc/QCReturnModal';
import QCLifecycleDrawer from '../components/qc/QCLifecycleDrawer';
import QCEscalateModal from '../components/qc/QCEscalateModal';
import QCPagination from '../components/qc/QCPagination';
import { T, KPI_TONES, KPICard, AgingBadge, ReturnedChip, isReturnedRow, AssignedCell, PriorityPill, IconAction } from '../components/qc/QCQueueUI';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

/* ── Tabs ─────────────────────────────────────────────────── */
// Feb 2026 — QC Phase 1: user-facing label is "Correction Required" everywhere
// in the app. Internal tab id `returned_for_correction` is unchanged for API
// parity with `GET /api/qc/orders?tab=returned_for_correction`.
// Jun 14, 2026 — QCC-001: "My Queue" → "Files Awaiting Review" (user-requested
// rename as part of the Quality Command Center rebrand). Internal id
// `my_queue` is preserved to avoid touching the backend/state machine.
// Feb 2026 — QC-CONSOLIDATE-001: "Rejected Today" → "Critical Returns Today".
// The tab id `rejected_today` is preserved on the backend; the count is
// now sourced from `stats.critical_returns_today`.
// QC-SPECIALIST-UI-PREMIUM-001 (Oct 2026): the KPI cards ARE the tabs now
// (secondary pill bar removed). `count` resolves the matching stats key.
const QC_TABS = [
  { id: 'my_queue',                label: 'Files Awaiting Review',  icon: FileText,      tone: 'blue',    testId: 'qc-kpi-my-queue',               count: (s) => s?.my_queue_count },
  { id: 'unassigned',              label: 'Unassigned',             icon: UserRoundX,    tone: 'slate',   testId: 'qc-kpi-unassigned',             count: (s) => s?.unassigned_count },
  { id: 'returned_for_correction', label: 'Correction Required',    icon: AlertCircle,   tone: 'rose',    testId: 'qc-kpi-returned',               count: (s) => s?.returned_for_correction },
  { id: 'approved_today',          label: 'Approved Today',         icon: CheckCircle,   tone: 'emerald', testId: 'qc-kpi-approved-today',         count: (s) => s?.approved_today },
  { id: 'rejected_today',          label: 'Critical Returns Today', icon: AlertTriangle, tone: 'red',     testId: 'qc-kpi-critical-returns-today', count: (s) => s?.critical_returns_today ?? s?.rejected_today },
];

/* ── Simple filter dropdown ───────────────────────────────── */
const FilterDropdown = ({ label, value, options, onChange, placeholder = "All" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const display = value ? (options.find(o => o.value === value)?.label || value) : placeholder;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} type="button" aria-expanded={open}
        data-testid={`qc-filter-${label.toLowerCase()}`}
        className={`flex items-center gap-1.5 h-10 px-3 text-[13px] border rounded-lg bg-white min-w-[130px] shadow-sm ${T}
          hover:border-slate-300 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200
          ${open || value ? 'border-blue-300' : 'border-slate-200'}`}>
        <span className="text-slate-400">{label}:</span>
        <span className="font-semibold text-slate-700 truncate">{display}</span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[180px] bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1 animate-in fade-in-0 zoom-in-95 duration-150">
          <button onClick={() => { onChange(''); setOpen(false); }} type="button"
            className={`w-full px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-slate-50 ${!value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}>
            {placeholder}
          </button>
          {options.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} type="button"
              className={`w-full px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-slate-50 ${value === o.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Helpers ──────────────────────────────────────────────── */
const initials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'QC';
const roleName = (r) => ({
  qc_specialist: 'QC Specialist', admin: 'Administrator',
  tax_search_team_leader: 'Tax Search Team Leader',
  title_search_team_leader: 'Title Search Team Leader',
})[r] || r?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User';

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════ */
const QCDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // PREMIUM-NOTIFY-001 — QC lands here (not on /dashboard): show the welcome toast once per login session
  useEffect(() => {
    if (user && sessionStorage.getItem('showWelcomeBanner') === 'true') {
      sessionStorage.removeItem('showWelcomeBanner');
      setTimeout(() => showGlassmorphismWelcome(user.full_name || user.email, user.role), 500);
    }
  }, [user]);

  const [config, setConfig] = useState({ sla_threshold_hours: 4, qc_enabled: false });
  const [filterOpts, setFilterOpts] = useState({ clients: [], products: [], submitters: [], priorities: [] });
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('my_queue');

  const { searchInput, setSearchInput, debouncedValue: debouncedSearch } = useDebouncedSearch('', 300);
  const [filters, setFilters] = useState({ priority: '', client: '', product: '' });

  /* ── QCC-PAGINATE-001 (Feb 2026) — pagination state ────── */
  // per_page is persisted in localStorage so reviewers don't have to reselect
  // their preferred page size every session. Allowed: 25 / 50 / 100.
  const PER_PAGE_KEY = 'qc.dashboard.perPage';
  const readPerPage = () => {
    const raw = parseInt(localStorage.getItem(PER_PAGE_KEY) || '25', 10);
    return [25, 50, 100].includes(raw) ? raw : 25;
  };
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(readPerPage());
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [lifecycleOrder, setLifecycleOrder] = useState(null);
  const [showEscalate, setShowEscalate] = useState(false);
  const [assigning, setAssigning] = useState({});
  const [releasing, setReleasing] = useState({});

  /* ── Bootstrap: config + filter options (once) ─────────── */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const [cfgRes, fRes] = await Promise.all([
          fetch(`${API}/qc/config`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/qc/filter-options`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cfgRes.ok) setConfig(await cfgRes.json());
        if (fRes.ok) setFilterOpts(await fRes.json());
      } catch (e) { console.error('[QC] config fetch:', e); }
    })();
  }, []);

  /* ── Fetch KPIs ─────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/qc/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('[QC] stats:', e); }
  }, []);

  /* ── Fetch orders ───────────────────────────────────────── */
  const fetchOrders = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const p = new URLSearchParams({
        // QCC-QUEUE-002 (Jun 2026): REVERT of QCC-QUEUE-001. "Files Awaiting
        // Review" is the specialist's PERSONAL claimed queue (backend
        // `my_queue`, scoped to qc_assigned_to = me). Freshly submitted
        // orders belong in "Unassigned" until a specialist clicks Claim —
        // the Aug 25 remap to the global `qc_review` tab leaked every
        // specialist's claimed files into everyone's personal queue.
        tab: activeTab,
        sort_by: 'time_in_qc',
        sort_order: 'asc',
        page: String(page),
        per_page: String(perPage),
      });
      if (debouncedSearch) p.append('search', debouncedSearch);
      if (filters.priority) p.append('priority', filters.priority);
      if (filters.client) p.append('client_code', filters.client);
      if (filters.product) p.append('product', filters.product);

      const res = await fetch(`${API}/qc/orders?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setOrders(d.orders || []);
        // QCC-PAGINATE-001 (Feb 2026): track total + pages so the banner and
        // pagination strip can render even on tabs where the table is empty.
        setTotalCount(typeof d.total_count === 'number' ? d.total_count : (d.orders || []).length);
        setTotalPages(typeof d.total_pages === 'number' ? Math.max(1, d.total_pages) : 1);
        // If the server clamped us to a different page (e.g. we deleted the
        // last item on the last page), keep our state in sync.
        if (typeof d.page === 'number' && d.page !== page) setPage(d.page);
        setLastRefresh(new Date());
      } else if (res.status === 403) {
        setError('Permission denied.');
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to load orders');
      }
    } catch { setError('Network error.'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [activeTab, debouncedSearch, filters, page, perPage]);

  useEffect(() => { fetchStats(); fetchOrders(); }, [fetchStats, fetchOrders]);

  /* QCC-PAGINATE-001: reset to page 1 whenever the user changes tab, search,
     or any filter. perPage changes also reset (the useEffect on `page` setter
     handles that via the perPage change handler below). */
  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, filters.priority, filters.client, filters.product]);

  const handlePerPageChange = useCallback((next) => {
    setPerPage(next);
    setPage(1);
    try { localStorage.setItem(PER_PAGE_KEY, String(next)); } catch { /* ignore quota errors */ }
  }, []);

  /* ── Actions ────────────────────────────────────────────── */
  const assignToMe = async (o) => {
    setAssigning(p => ({ ...p, [o.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${o.id}/qc-assign`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success(`${o.order_number} assigned`); fetchStats(); fetchOrders(true); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Assign failed'); }
    } catch { toast.error('Network error'); }
    finally { setAssigning(p => ({ ...p, [o.id]: false })); }
  };

  const release = async (o) => {
    setReleasing(p => ({ ...p, [o.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${o.id}/qc-unassign`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success(`${o.order_number} released`); fetchStats(); fetchOrders(true); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Release failed'); }
    } catch { toast.error('Network error'); }
    finally { setReleasing(p => ({ ...p, [o.id]: false })); }
  };

  const approveClick = (o) => {
    if (o.qc_submitted_by === user?.id) { toast.error('Separation of duties: cannot approve your own submission.'); return; }
    if (!o.qc_assigned_to) { toast.error('Order must be assigned before approving. Click "Assign to Me" first.'); return; }
    setSelectedOrder(o); setShowApprove(true);
  };
  const approveConfirm = async (notes, extra = {}) => {
    if (!selectedOrder) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${selectedOrder.id}/qc-approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || null, ...extra }),
      });
      if (res.ok) { toast.success(`${selectedOrder.order_number} approved!`); setTimeout(() => { setShowApprove(false); setSelectedOrder(null); fetchStats(); fetchOrders(true); }, 900); return true; }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail?.message || d.detail || 'Approve failed', { duration: d.detail?.error === 'TAX_NARRATIVE_MISSING' ? 12000 : 4000 }); }
    } catch { toast.error('Network error'); }
    return false;
  };

  // QC-CONSOLIDATE-001 (Feb 2026): rejectClick removed.
  // All hard rejections now flow through Return-for-Correction with
  // severity='critical' (handled by returnClick + QCReturnModal).

  const returnClick = (o) => {
    if (!o.qc_assigned_to || o.qc_assigned_to !== user?.id) {
      toast.error('Order must be assigned to you before returning. Click "Assign to Me" first.');
      return;
    }
    setSelectedOrder(o); setShowReturn(true);
  };

  const returnConfirm = async (category, errorType, notes, evidenceFileIds, severity, affectedComponents = null) => {
    if (!selectedOrder || !category || !notes) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${selectedOrder.id}/qc-return`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejection_category: category,
          error_type: errorType || null,
          rejection_reason: notes,
          evidence_file_ids: evidenceFileIds || [],
          severity: severity || null,
          affected_components: affectedComponents || null,
        }),
      });
      if (res.ok) {
        toast.success(`${selectedOrder.order_number} returned for correction.`);
        setShowReturn(false); setSelectedOrder(null); fetchStats(); fetchOrders(true);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Return failed');
      }
    } catch { toast.error('Network error'); }
  };
  // QC-CONSOLIDATE-001 (Feb 2026): rejectConfirm removed (consolidated into returnConfirm).

  const escalateClick = (o) => { setSelectedOrder(o); setShowEscalate(true); };
  const escalateConfirm = async (reason) => {
    if (!selectedOrder || !reason) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${selectedOrder.id}/qc-escalate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ escalation_reason: reason }),
      });
      if (res.ok) { toast.success(`${selectedOrder.order_number} escalated.`); setShowEscalate(false); setSelectedOrder(null); fetchStats(); fetchOrders(true); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Escalate failed'); }
    } catch { toast.error('Network error'); }
  };

  // LOGOUT-FLICKER-001: same-tick logout + SPA navigate (no await) so the
  // auth-state clear and route transition batch into one atomic render.
  const handleLogout = () => { try { logout({ skipRedirect: true }); } catch { /* ignore */ } navigate('/login', { replace: true }); };

  /* ── Filter options ─────────────────────────────────────── */
  const priorityOpts = filterOpts.priorities?.map(p => ({ value: p, label: p.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) })) || [];
  const clientOpts = filterOpts.clients?.map(c => ({ value: c, label: c })) || [];
  const productOpts = filterOpts.products?.map(p => ({ value: p, label: p })) || [];

  const showActions = activeTab === 'my_queue' || activeTab === 'unassigned';

  /* ── QC disabled ────────────────────────────────────────── */
  if (!config.qc_enabled && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">QC Workflow Disabled</h1>
          <p className="text-gray-600 mb-6">The QC workflow feature is currently disabled.</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-slate-50" data-testid="qc-dashboard">
      <OpsIntroOverlay />
      {/* ─── Header ──────────────────────────────────────────
          Jun 14, 2026 — QCC-001: TitleFlow brand anchor + Quality
          Command Center title. Uses the frosted "header-frosted"
          treatment from App.css so it harmonises with the Admin
          Dashboard, then a vertical slate divider before the title
          per the design blueprint. ────────────────────────────── */}
      {/* ─── QCC-UI-003 (Jun 2026): TitleFlow blue application shell header —
          identical structure/classes to the Dashboard shell used by Typing
          Operations (App.js L2522+, `.header-blue` + `.user-badge` from
          App.css). Left: logo | Dashboard | My Workspace. Center: WFM
          cluster (Active / Break / Downtime). Right: bell + profile chip.
          All QC behavior, permissions and testids unchanged. */}
      <header className="header-blue sticky top-0 z-30">
        <div className="mx-auto px-6" style={{ maxWidth: '1440px' }}>
          <div className="flex items-center justify-between h-14">
            {/* LEFT ZONE: Logo + Primary Navigation */}
            <div className="flex items-center gap-1 pl-1 min-w-0 shrink-0">
              <TitleFlowLogo
                variant="darkBright"
                className="cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                style={{ height: '32px', width: 'auto' }}
                onClick={() => navigate('/qc-dashboard')}
                data-testid="header-logo"
                aria-label="TitleFlow"
              />
              <div className="h-5 w-px bg-slate-200 mx-1.5 shrink-0" />
              <button
                onClick={() => navigate('/qc-dashboard')}
                data-testid="nav-dashboard-btn"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap bg-blue-50 text-blue-700 border border-blue-200"
              >
                <ClipboardList className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">Dashboard</span>
              </button>
              {/* My Workspace permanent nav button — QC-WFM-001 */}
              <button
                onClick={() => navigate('/dashboard/workspace')}
                data-testid="qc-nav-workspace-btn"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <User className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">My Workspace</span>
              </button>
            </div>

            {/* CENTER ZONE: Workforce-management cluster — QC-WFM-001 */}
            <div className="flex items-center gap-2 shrink-0">
              <SessionHealthIndicator />
              <BreakButton />
              <SystemDowntimeButton />
            </div>

            {/* RIGHT ZONE: Notifications & Profile */}
            <div className="flex items-center gap-2">
              {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local refresh clock */}
              {lastRefresh && <span className="text-xs text-white/60 hidden xl:inline whitespace-nowrap">Updated {lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>}
              <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchOrders(true); }} disabled={refreshing}
                className="h-8 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white" data-testid="qc-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>

              {/* WS-SWITCHER-POS-001: shared fixed position — switcher → bell → profile,
                  identical to the Dashboard shell (App.js) and AdminLayout headers. */}
              <WorkspaceSwitcher user={user} />
              <NotificationBell user={user} />

              {/* Profile — shared `.user-badge` chip (white-on-blue via .header-blue) */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <div className="user-badge cursor-pointer" data-testid="qc-profile-dropdown-trigger">
                    <div className="initials">{initials(user?.full_name)}</div>
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-xs font-semibold max-w-[140px] truncate">{user?.full_name || 'QC User'}</span>
                      <span className="text-[10px] opacity-75">{roleName(user?.role)}</span>
                    </div>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 mt-1" data-testid="qc-profile-dropdown-menu">
                  {/* QCC-UI-004/r2: name+role header removed too — already shown
                      on the profile button. Dropdown = Logout only. */}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="qc-dropdown-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* QC-SPECIALIST-UI-PREMIUM-001 — full-width premium layout. The KPI
          cards act as the queue tabs; Submitted Date/Time column removed
          (display only — `qc_submitted_at` still drives Aging + sort). */}
      <div className="w-full px-4 sm:px-6 xl:px-8 2xl:px-10 py-5 space-y-4" data-testid="qc-dashboard-body">
        {/* Title + KPI cards */}
        <div className="flex flex-col xl:flex-row xl:items-center gap-4">
          <div className="xl:w-[260px] 2xl:w-[300px] flex-shrink-0">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900 leading-tight" data-testid="qc-page-title">
              QC Specialist
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5" data-testid="qc-page-subtitle">
              Review, approve and manage returned files.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 flex-1 min-w-0" data-testid="qc-kpi-cards">
            {QC_TABS.map(t => (
              <KPICard key={t.id} icon={t.icon} label={t.label} value={t.count(stats) ?? 0}
                tone={KPI_TONES[t.tone]} isActive={activeTab === t.id}
                onClick={() => setActiveTab(t.id)} testId={t.testId} />
            ))}
          </div>
        </div>

        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-2.5" data-testid="qc-filter-bar">
          <FilterDropdown label="Client" value={filters.client} options={clientOpts} onChange={v => setFilters(f => ({ ...f, client: v }))} />
          <FilterDropdown label="Product" value={filters.product} options={productOpts} onChange={v => setFilters(f => ({ ...f, product: v }))} />
          <FilterDropdown label="Priority" value={filters.priority} options={priorityOpts} onChange={v => setFilters(f => ({ ...f, priority: v }))} />
          <div className="relative ml-auto flex-1 min-w-[240px] max-w-xl">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input type="text" placeholder="Search order #, client code, product, address…"
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              className={`h-10 w-full text-[13px] border border-slate-200 rounded-lg pl-9 pr-3 bg-white shadow-sm ${T}
                hover:border-slate-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100`}
              data-testid="qc-search-input" />
          </div>
          <button type="button"
            onClick={() => { setFilters({ priority: '', client: '', product: '' }); setSearchInput(''); }}
            disabled={!(filters.priority || filters.client || filters.product || searchInput)}
            data-testid="qc-clear-filters"
            className={`h-10 px-3.5 inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm ${T}
              hover:border-slate-300 hover:text-slate-900 hover:shadow disabled:opacity-40 disabled:pointer-events-none`}>
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Clear
          </button>
        </div>

        {/* Queue card */}
        <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm transition-opacity duration-200 ${refreshing && !loading ? 'opacity-60' : ''}`} data-testid="qc-queue-card">
          {error && (
            <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-slate-800 mb-1">
                {activeTab === 'my_queue' ? 'Your queue is clear' : activeTab === 'unassigned' ? 'No unassigned orders' : 'No orders'}
              </h2>
              <p className="text-slate-500 text-sm">
                {activeTab === 'unassigned' ? 'All orders have been claimed.' : 'Nothing to review right now.'}
              </p>
            </div>
          ) : (
            <>
              {/* QCC-PAGINATE-001 (Feb 2026) — Phase A: visibility banner. */}
              {totalCount > orders.length && (
                <div
                  className="flex items-start gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200 text-amber-900"
                  role="status"
                  data-testid="qc-pagination-banner"
                >
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" />
                  <div className="text-sm leading-snug">
                    <div className="font-semibold">
                      Showing {orders.length} of {totalCount.toLocaleString()} records
                    </div>
                    <div className="text-amber-800/90">
                      Refine filters or navigate pages below to view additional records.
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed min-w-[1100px]" data-testid="qc-orders-table">
                <colgroup>
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '10%' }} />
                  {showActions && <col style={{ width: '10%' }} />}
                  <col style={{ width: '9%' }} />
                  <col style={{ minWidth: '300px' }} />
                </colgroup>
                <thead>
                  <tr className="text-left text-[11px] text-white/95 uppercase tracking-[0.08em] font-semibold"
                    style={{ background: 'linear-gradient(135deg, #003c8f 0%, #004aad 55%, #0d55b8 100%)' }}>
                    <th className="px-5 py-3.5">Order #</th>
                    <th className="px-3 py-3.5">Client Code</th>
                    <th className="px-3 py-3.5">Product</th>
                    <th className="px-3 py-3.5">Submitted By</th>
                    <th className="px-3 py-3.5">Aging</th>
                    {showActions && <th className="px-3 py-3.5">Assigned</th>}
                    <th className="px-3 py-3.5">Priority</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map(o => {
                    const assignedToMe = o.qc_assigned_to === user?.id;
                    const assignedOther = o.qc_assigned_to && o.qc_assigned_to !== user?.id;
                    const unassigned = !o.qc_assigned_to;
                    // TYPING-RETURN-TO-QC-001 — QC aging clock restarts at re-entry for Typing returns
                    const qcClock = (o.typing_status === 'returned_to_qc' && o.qc_reentered_at) ? o.qc_reentered_at : o.qc_submitted_at;
                    const returned = isReturnedRow(o);

                    return (
                      <tr key={o.id}
                        className={`${T} ${returned ? 'bg-rose-50/40 hover:bg-rose-50/70' : 'hover:bg-slate-50'}`}
                        data-testid={`qc-order-row-${o.id}`} data-returned={returned ? 'true' : 'false'}>
                        <td className="px-5 py-3 align-middle">
                          <div className="flex flex-col items-start gap-1 min-w-0">
                            <span className="font-semibold text-[14px] text-[#004aad] truncate max-w-full" title={o.order_number}>{o.order_number}</span>
                            <ReturnedChip order={o} />
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle">
                          <span className="text-[13.5px] font-medium text-slate-800 truncate block" title={o.client_short_code || o.client_code || ''}>{o.client_short_code || o.client_code || 'N/A'}</span>
                        </td>
                        <td className="px-3 py-3 align-middle">
                          <span className="text-[13.5px] text-slate-700 line-clamp-2 leading-snug" title={o.client_product || ''}>{o.client_product || 'N/A'}</span>
                        </td>
                        <td className="px-3 py-3 align-middle">
                          <div className="text-[13.5px] text-slate-700 flex items-center gap-2 min-w-0">
                            <User className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate" title={o.qc_submitted_by_name || 'Unknown'}>
                              {o.qc_submitted_by_name || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle">
                          <AgingBadge submittedAt={qcClock} slaThresholdHours={config.sla_threshold_hours} />
                        </td>
                        {showActions && (
                          <td className="px-3 py-3 align-middle">
                            <AssignedCell unassigned={unassigned} assignedToMe={assignedToMe} name={o.qc_assigned_to_name} />
                          </td>
                        )}
                        <td className="px-3 py-3 align-middle"><PriorityPill priority={o.priority} /></td>
                        <td className="px-5 py-3 align-middle">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* View — primary */}
                            <button type="button"
                              onClick={() => navigate(`/orders/${o.id}/fulfillment?qc_view=true`)}
                              data-testid={`qc-view-${o.id}`}
                              className={`h-8 px-3 inline-flex items-center gap-1.5 rounded-lg bg-[#004aad] text-white text-[13px] font-semibold shadow-sm ${T}
                                hover:bg-[#003c8f] hover:shadow hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300`}>
                              <FileText className="w-3.5 h-3.5" aria-hidden="true" /> View
                            </button>

                            {showActions && (
                              <>
                                {unassigned && (
                                  <IconAction icon={UserPlus} label="Assign to Me" tone="blue"
                                    onClick={() => assignToMe(o)} disabled={assigning[o.id]} testId={`qc-assign-${o.id}`} />
                                )}
                                {assignedToMe && (
                                  <IconAction icon={UserMinus} label="Release"
                                    onClick={() => release(o)} disabled={releasing[o.id]} testId={`qc-release-${o.id}`} />
                                )}

                                {/* QCC-CLEANUP-001: Approve / Return / Reject live inside the order review screen. */}
                                <IconAction icon={Clock} label="History"
                                  onClick={() => setLifecycleOrder(o)} testId={`qc-lifecycle-${o.id}`} />

                                {/* Escalate — QCC-CLEANUP-002: top-level, assignee only. */}
                                {assignedToMe && (
                                  <button type="button"
                                    onClick={() => escalateClick(o)}
                                    data-testid={`qc-escalate-${o.id}`}
                                    title="Escalate to Team Leader"
                                    className={`h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white text-purple-700 text-[13px] font-medium shadow-sm ${T}
                                      hover:bg-purple-50 hover:border-purple-300 hover:shadow hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-200`}>
                                    <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" /> Escalate
                                  </button>
                                )}

                                {assignedOther && (
                                  <span className="text-xs text-slate-400 italic ml-1" title={`Assigned to ${o.qc_assigned_to_name}`}>
                                    Locked
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {/* QCC-PAGINATE-001 (Feb 2026) — Phase B: pagination footer. */}
              <QCPagination
                page={page}
                perPage={perPage}
                totalCount={totalCount}
                totalPages={totalPages}
                currentCount={orders.length}
                onPageChange={setPage}
                onPerPageChange={handlePerPageChange}
              />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <QCApproveModal isOpen={showApprove} onClose={() => { setShowApprove(false); setSelectedOrder(null); }} onConfirm={approveConfirm} order={selectedOrder} />
      <QCReturnModal isOpen={showReturn} onClose={() => { setShowReturn(false); setSelectedOrder(null); }} onConfirm={returnConfirm} order={selectedOrder} />
      <QCLifecycleDrawer
        orderId={lifecycleOrder?.id}
        orderNumber={lifecycleOrder?.order_number}
        isOpen={!!lifecycleOrder}
        onClose={() => setLifecycleOrder(null)}
      />
      <QCEscalateModal isOpen={showEscalate} onClose={() => { setShowEscalate(false); setSelectedOrder(null); }} onConfirm={escalateConfirm} order={selectedOrder} />
    </div>
  );
};

export default QCDashboard;
